'use client';

import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  Badge,
  BadgeSeverity,
  Splitter,
  IconButton,
  MetaDefault,
  MonoSmall,
  Table,
  Tooltip,
  Menu,
  getStatusSeverity,
  getMethodSeverity,
} from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  Alert01Icon,
  BotIcon,
  Globe02Icon,
  Message01Icon,
  MoreVerticalCircle01Icon,
  HierarchyIcon,
  Edit03Icon,
  ReplayIcon,
} from '@terra/ui-icons';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type {
  ColDef,
  ICellRendererParams,
  GridApi,
  CellStyle,
  IRowNode,
  ModelUpdatedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { TABLE_IDS, toasterApi } from '@terra/ui-design-system';
import { EmptyState } from '../EmptyState';
import { shouldShowHttpHistoryEmptyState } from './http-history-empty-state';
import {
  SelectHttpContextForChatDialog,
  type SelectHttpContextForChatDialogState,
} from '../dialogs/SelectHttpContextForChatDialog';
import {
  useProxyStore,
  type HighlightColor,
  type HttpRequest,
} from '../../store/proxy-store';
import { useElectronProxy } from '../../hooks/useElectronProxy';
import { useChatStore } from '../../store/chatStore';
import { RequestsFilterBar } from './RequestsFilterBar';
import { useHttpReplay } from '../../hooks/useHttpReplay';
import {
  buildTabPageScrollKey,
  useLayoutStore,
  useMainContentTabs,
} from '../../store/layoutStore';
import {
  useSelectedTarget,
  useOptionalTargetId,
  useTargetType,
  useProjectStore,
} from '../../store/projectStore';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useDecoder } from '../../hooks/useDecoder';
import { useComparer } from '../../hooks/useComparer';
import { registerRequestGridHandler } from '../../hooks/useReplayKeyboard';
import { CommentDialog } from '../annotations/CommentDialog';
import { TagsDialog } from '../annotations/TagsDialog';
import { getHighlightHex, getHighlightRowTint } from '../annotations/constants';
import { useRequestBodies } from '../../hooks/useRequestBodies';
import { useLazyBody } from '../../hooks/useLazyBody';
import {
  isRequestsSortField,
  useTabScopedRequestsFilters,
} from '../../store/tab/requestsTabScopedFilterStore';
import { useFilterPresetsStore } from '../../store/filterPresetsStore';
import { useExclusionRulesStore } from '../../store/exclusionRulesStore';
import {
  useTrackRequest,
  extractStatusCode,
} from '../../hooks/useTrackRequest';
import { useBulkTrackRequests } from '../../hooks/useBulkTrackRequests';
import { useManualTrackWithCredentials } from '../../hooks/useManualTrackWithCredentials';
import { GlobalRequestDetailsPanel } from '../GlobalRequestDetailsPanel';
import {
  createHttpRequestMention,
  createHttpResponseMention,
} from '../../utils/httpMentionUtils';
import { truncateBodyForChat } from '../../utils/httpFormatter';
import {
  useHistoryDatasource,
  resolveHistoryTarget,
} from '../../hooks/useHistoryDatasource';
import { useAgGridScrollPreservation } from '../../hooks/useAgGridScrollPreservation';
import { useTabViewStateStore } from '../../store/tabViewStateStore';
import { useChatTabs } from '../../store/chatTabsStore';
import { useConversations } from '../../store/conversations-store';
import {
  BULK_HTTP_REQUEST_LIMIT,
  HttpHistoryBulkActionsBar,
} from './HttpHistoryBulkActionsBar';
import { HttpRequestActionsMenuContent } from './HttpRequestActionsMenuContent';
import type { ApplicationScopeFilter } from '../../electron/search-db';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

type ParsedUrlData = {
  pathname: string;
  search: string;
  extension: string;
};
const ROW_HEIGHT = 40;
const REQUEST_GRID_COLUMN_ID = {
  ACTIONS: 'actions',
  BULK_SELECT: 'bulkSelect',
  HIGHLIGHT: 'highlight',
  NOTES: 'notes',
} as const;
const urlParseCache = new Map<string, ParsedUrlData | null>();
const URL_CACHE_MAX_SIZE = 1000;

function getParsedUrl(url: string): ParsedUrlData | null {
  const cached = urlParseCache.get(url);

  if (cached !== undefined) return cached;

  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const lastDot = pathname.lastIndexOf('.');
    const lastSlash = pathname.lastIndexOf('/');
    const extension =
      lastDot > lastSlash && lastDot !== -1 ? pathname.substring(lastDot) : '';
    const result: ParsedUrlData = {
      pathname,
      search: urlObj.search,
      extension,
    };

    if (urlParseCache.size >= URL_CACHE_MAX_SIZE) {
      const firstKey = urlParseCache.keys().next().value;

      if (firstKey) urlParseCache.delete(firstKey);
    }

    urlParseCache.set(url, result);

    return result;
  } catch {
    urlParseCache.set(url, null);

    return null;
  }
}

type ProxyRequest = HttpRequest;
type SourceConfig = {
  color: string;
  icon: typeof BotIcon;
  label: string;
};
const SOURCE_CONFIG: Record<string, SourceConfig> = {
  copilot: { color: 'purple', icon: Message01Icon, label: 'Copilot' },
  neo: { color: 'blue', icon: BotIcon, label: 'Neo' },
  replay: { color: 'teal', icon: ReplayIcon, label: 'Replay' },
  proxy: { color: 'gray', icon: Globe02Icon, label: 'Proxy' },
};

function getSourceConfig(source: string): SourceConfig {
  return SOURCE_CONFIG[source] || SOURCE_CONFIG['proxy'];
}

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatTime = (timestamp: Date | string | number): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const dateStr = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return `${dateStr} ${timeStr}`;
};

type GlobalRequestsTabProps = {
  tabId: string;
};

export function GlobalRequestsTab({ tabId }: GlobalRequestsTabProps) {
  const t = useTranslations();
  const {
    settings,
    updateSettings,
    setRequestHighlight,
    selectedRequest: storeSelectedRequest,
    setSelectedRequest: setStoreSelectedRequest,
  } = useProxyStore();
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const { clearHistory } = useElectronProxy();
  const { appendToInput, sendHttpMentionsToChat } = useChatStore();
  const selectedTarget = useSelectedTarget();
  const targetId = useOptionalTargetId();
  const targetType = useTargetType();
  const { isFeatureFlagEnabled: boothTourEnabled } =
    useFeatureFlags('BOOTH_TOUR_ENABLED');

  useEffect(() => {
    const proxyState = useProxyStore.getState();

    if (
      targetId &&
      (targetId !== proxyState.currentTargetId ||
        targetType !== proxyState.currentTargetType)
    ) {
      useProxyStore.getState().setCurrentTarget({ targetId, targetType });
    }
  }, [targetId, targetType]);
  const { userId } = useCurrentUser();
  const { openDecoderWith } = useDecoder();
  const { addRequestToComparer } = useComparer();
  const { trackRequest, trackRequests, isTracking } = useTrackRequest();
  const { trackBulkRequests, isBulkTracking } = useBulkTrackRequests({
    trackRequests,
    isTracking,
  });
  const { requestManualTrack } = useManualTrackWithCredentials();
  const globalRequestsSplitterSizes = useLayoutStore(
    (state) => state.globalRequestsSplitterSizes,
  );
  const { navigateInActiveTab } = useMainContentTabs();
  const historyScrollId = useLayoutStore((state) => {
    const navigation = state.tabNavigationByTabId[tabId];
    const pageIndex = navigation?.currentIndex ?? 0;
    const pageId = navigation?.entries[pageIndex]?.pageId ?? tabId;

    return `${buildTabPageScrollKey({ tabId, pageId, pageIndex })}:http-history`;
  });
  const { chatTabs, addChatTab, setActiveChatTabId, updateChatTab } =
    useChatTabs();
  const {
    conversations,
    refetch: refetchConversations,
    setActiveConversation,
  } = useConversations({
    targetId,
  });
  const setSidePanelsVisible = useLayoutStore(
    (state) => state.setSidePanelsVisible,
  );
  const setRightPanelView = useLayoutStore((state) => state.setRightPanelView);
  const setChatCollapsed = useLayoutStore((state) => state.setChatCollapsed);
  const setGlobalRequestsSplitterSizes = useLayoutStore(
    (state) => state.setGlobalRequestsSplitterSizes,
  );
  const selectedRequest = storeSelectedRequest as ProxyRequest | null;
  const setSelectedRequest = setStoreSelectedRequest;
  const [httpContextDialogState, setHttpContextDialogState] =
    useState<SelectHttpContextForChatDialogState | null>(null);
  const handleHttpContextDialogClose = useCallback(() => {
    setHttpContextDialogState(null);
  }, []);
  const [gridApi, setGridApi] = useState<GridApi<ProxyRequest> | null>(null);
  const [selectedBulkRequests, setSelectedBulkRequests] = useState<
    ProxyRequest[]
  >([]);
  const [availableBulkRequestCount, setAvailableBulkRequestCount] = useState(0);
  const [isBulkActionRunning, setIsBulkActionRunning] = useState(false);
  const {
    sortField,
    sortOrder,
    sourceFilter,
    setSourceFilter,
    setSortField,
    setSortOrder,
    resetFilters,
  } = useTabScopedRequestsFilters();
  const showOnlyInScope = settings.showOnlyInScopeRequests;
  const applicationScope = useMemo((): ApplicationScopeFilter | undefined => {
    if (!showOnlyInScope || !selectedTarget) return undefined;

    return {
      applicationUrl: selectedTarget.url ?? '',
      domainsScope: selectedTarget.domains ?? [],
    };
  }, [showOnlyInScope, selectedTarget]);
  const {
    datasource,
    cacheBlockSize,
    maxBlocksInCache,
    filteredCount,
    isLoadingGrid,
    isLoadingCount,
  } = useHistoryDatasource({
    gridApi,
    applicationId: targetId,
    sourceFilter,
    applicationScope,
    containerRef: gridContainerRef,
  });

  useAgGridScrollPreservation({
    gridApi,
    containerRef: gridContainerRef,
    scrollId: historyScrollId,
    isLoadingGrid,
  });
  const lastPositiveCountRef = useRef<number>(0);
  const prevTargetIdRef = useRef(targetId);

  if (prevTargetIdRef.current !== targetId) {
    prevTargetIdRef.current = targetId;
    lastPositiveCountRef.current = 0;
  }

  if (filteredCount !== null && filteredCount > 0) {
    lastPositiveCountRef.current = filteredCount;
  }

  const { fetchBodies, resolveRequestBodyForTransport, getBodies, cacheSize } =
    useRequestBodies();
  const requestLazyBody = useLazyBody({
    bodyRef: selectedRequest?.bodyRef,
    bodyPreview: selectedRequest?.bodyPreview,
    inlineBody: selectedRequest?.body,
    bodyStorageType: selectedRequest?.bodyStorageType,
    bodyContentType: selectedRequest?.bodyContentType,
    bodySize: selectedRequest?.bodySize,
  });
  const responseLazyBody = useLazyBody({
    bodyRef: selectedRequest?.responseBodyRef,
    bodyPreview: selectedRequest?.responseBodyPreview,
    inlineBody: selectedRequest?.responseBody,
    bodyStorageType: selectedRequest?.responseBodyStorageType,
    bodyContentType: selectedRequest?.responseBodyContentType,
    bodySize: selectedRequest?.responseBodySize,
  });

  useEffect(() => {
    const requestId = selectedRequest?.id;

    if (!requestId) return;
    const hasFileStoredRequest =
      selectedRequest.bodyStorageType === 'file' || !!selectedRequest.bodyRef;
    const hasFileStoredResponse =
      selectedRequest.responseBodyStorageType === 'file' ||
      !!selectedRequest.responseBodyRef;

    if (hasFileStoredRequest && hasFileStoredResponse) return;
    const cached = getBodies(requestId);

    if (cached) {
      const currentRequest = useProxyStore.getState().selectedRequest;

      if (currentRequest?.id === requestId) {
        const newBody = hasFileStoredRequest
          ? currentRequest.body
          : cached.requestBody || '';
        const newResponseBody = hasFileStoredResponse
          ? currentRequest.responseBody
          : cached.responseBody;
        const needsUpdate =
          currentRequest.body !== newBody ||
          currentRequest.responseBody !== newResponseBody;

        if (needsUpdate) {
          setSelectedRequest({
            ...currentRequest,
            body: newBody,
            responseBody: newResponseBody,
          });
        }
      }

      return;
    }

    if (!hasFileStoredRequest || !hasFileStoredResponse) {
      fetchBodies(requestId).then((bodies) => {
        if (bodies) {
          const stillSelected = useProxyStore.getState().selectedRequest;

          if (stillSelected?.id === requestId) {
            setSelectedRequest({
              ...stillSelected,
              body: hasFileStoredRequest
                ? stillSelected.body
                : bodies.requestBody || '',
              responseBody: hasFileStoredResponse
                ? stillSelected.responseBody
                : bodies.responseBody,
            });
          }
        }
      });
    }
  }, [
    selectedRequest?.id,
    selectedRequest?.bodyStorageType,
    selectedRequest?.responseBodyStorageType,
    selectedRequest?.bodyRef,
    selectedRequest?.responseBodyRef,
    fetchBodies,
    getBodies,
    setSelectedRequest,
    cacheSize,
  ]);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [annotatingRequestId, setAnnotatingRequestId] = useState<string | null>(
    null,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRequestId, setMenuRequestId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [rowContextMenuOpen, setRowContextMenuOpen] = useState(false);
  const [rowContextMenuRequestId, setRowContextMenuRequestId] = useState<
    string | null
  >(null);
  const [rowContextMenuPosition, setRowContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const getGridRequest = useCallback(
    (requestId: string | null): ProxyRequest | undefined => {
      if (!requestId || !gridApi || gridApi.isDestroyed()) return undefined;

      return gridApi.getRowNode(requestId)?.data as ProxyRequest | undefined;
    },
    [gridApi],
  );
  const prevSelectedIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) return;
    const prevId = prevSelectedIdRef.current;
    const currentId = selectedRequest?.id;

    if (prevId === currentId) return;
    prevSelectedIdRef.current = currentId;
    const rowsToRedraw: IRowNode[] = [];

    if (prevId) {
      const prevNode = gridApi.getRowNode(prevId);

      if (prevNode) rowsToRedraw.push(prevNode);
    }

    if (currentId) {
      const currentNode = gridApi.getRowNode(currentId);

      if (currentNode) rowsToRedraw.push(currentNode);
    }

    if (rowsToRedraw.length > 0) {
      gridApi.redrawRows({ rowNodes: rowsToRedraw });
    }
  }, [gridApi, selectedRequest?.id]);
  const handleShowOnlyInScopeChange = useCallback(
    async (checked: boolean) => {
      updateSettings({ showOnlyInScopeRequests: checked });
      if (typeof window !== 'undefined' && window.electronAPI) {
        try {
          await window.electronAPI.proxy.updateConfig({
            showOnlyInScopeRequests: checked,
          });
        } catch (error) {
          console.error('Failed to update scope filter:', error);
        }
      }
    },
    [updateSettings],
  );
  const isApplyingPersistedSortRef = useRef(false);
  const applyTabSortState = useCallback(
    (api: GridApi<ProxyRequest>) => {
      isApplyingPersistedSortRef.current = true;
      api.applyColumnState({
        state: [{ colId: sortField, sort: sortOrder }],
        defaultState: { sort: null },
      });
      window.setTimeout(() => {
        isApplyingPersistedSortRef.current = false;
      }, 0);
    },
    [sortField, sortOrder],
  );

  useEffect(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      applyTabSortState(gridApi);
    }
  }, [gridApi, applyTabSortState]);
  const handleAddToContext = useCallback(
    (request: ProxyRequest) => {
      const requestMention = createHttpRequestMention({
        id: request.id,
        method: request.method,
        url: request.url,
        headers: request.headers,
        rawHeaders: request.rawHeaders,
        body: request.body,
        httpVersion: request.httpVersion,
      });

      if (request.status && request.responseHeaders) {
        setHttpContextDialogState({
          requestMention,
          responseMention: createHttpResponseMention({
            id: request.id,
            method: request.method,
            url: request.url,
            statusCode: request.status,
            statusText: request.responseStatusMessage,
            headers: request.responseHeaders,
            rawHeaders: request.responseRawHeaders,
            body: request.responseBody,
            httpVersion: request.responseHttpVersion,
          }),
        });

        return;
      }

      sendHttpMentionsToChat({
        mentions: [requestMention],
      });
    },
    [sendHttpMentionsToChat],
  );
  const { openInReplay } = useHttpReplay();
  const handleReplay = useCallback(
    async (request: ProxyRequest) => {
      const resolvedBody = await resolveRequestBodyForTransport({
        request,
        fallbackToBodyDb: true,
      });

      if (!resolvedBody) {
        toasterApi.error({
          title: 'Failed to load request body',
          description: 'The binary body could not be retrieved from storage.',
        });

        return;
      }

      const bodies = await fetchBodies(request.id);
      const responseBody = bodies?.responseBody ?? request.responseBody ?? '';

      openInReplay({
        method: request.method,
        url: request.url,
        headers: request.headers || {},
        rawHeaders: request.rawHeaders,
        body: resolvedBody.body,
        bodyEncoding: resolvedBody.bodyEncoding,
        httpVersion: request.httpVersion,
        responseStatus: request.status,
        responseStatusMessage: request.responseStatusMessage,
        responseHeaders: request.responseHeaders,
        responseBody,
        responseHttpVersion: request.responseHttpVersion,
      });
    },
    [fetchBodies, openInReplay, resolveRequestBodyForTransport],
  );
  const executeTrackRequest = useCallback(
    async ({
      request,
      credentialId,
      hideToast,
    }: {
      request: ProxyRequest;
      credentialId?: string;
      hideToast?: boolean;
    }): Promise<boolean> => {
      const resolvedBody = await resolveRequestBodyForTransport({
        request,
        fallbackToBodyDb: true,
      });

      if (!resolvedBody) {
        toasterApi.error({
          title: 'Failed to load request body',
          description: 'The binary body could not be retrieved from storage.',
        });

        return false;
      }

      const bodies = await fetchBodies(request.id);
      const responseBody = bodies?.responseBody || '';
      const statusCode = extractStatusCode(request);
      const timestamp =
        typeof request.timestamp === 'number'
          ? request.timestamp
          : typeof request.timestamp === 'string'
            ? new Date(request.timestamp).getTime()
            : request.timestamp.getTime();
      const result = await trackRequest({
        request: {
          method: request.method,
          url: request.url,
          headers: request.headers || {},
          body: resolvedBody.body,
          bodyEncoding: resolvedBody.bodyEncoding,
          httpVersion: request.httpVersion,
          timestamp,
        },
        response: {
          statusCode,
          statusMessage: request.responseStatusMessage || '',
          headers: request.responseHeaders || {},
          body: responseBody,
          duration: undefined,
          httpVersion: request.responseHttpVersion,
          timestamp,
        },
        credentialId,
        hideToast,
      });

      return result.success;
    },
    [trackRequest, fetchBodies, resolveRequestBodyForTransport],
  );
  const handleTrackRequest = useCallback(
    async (request: ProxyRequest) => {
      await requestManualTrack(({ credentialId }) =>
        executeTrackRequest({ request, credentialId }),
      );
    },
    [requestManualTrack, executeTrackRequest],
  );
  const selectedBulkRequestCount = selectedBulkRequests.length;
  const isBulkSelectionOverLimit =
    selectedBulkRequestCount > BULK_HTTP_REQUEST_LIMIT;
  const canRunBulkRequestAction =
    selectedBulkRequestCount > 0 && !isBulkSelectionOverLimit;
  const handleTrackSelectedRequests = useCallback(async () => {
    if (!canRunBulkRequestAction) {
      return;
    }

    await trackBulkRequests({ requests: selectedBulkRequests });
  }, [canRunBulkRequestAction, selectedBulkRequests, trackBulkRequests]);
  const handleSendSelectedToCopilot = useCallback(async () => {
    if (!canRunBulkRequestAction) {
      return;
    }

    setIsBulkActionRunning(true);

    try {
      const mentionResults = await Promise.all(
        selectedBulkRequests.map(async (request) => {
          const resolvedBody = await resolveRequestBodyForTransport({
            request,
            fallbackToBodyDb: true,
          });

          if (!resolvedBody) {
            return null;
          }

          return createHttpRequestMention({
            id: request.id,
            method: request.method,
            url: request.url,
            headers: request.headers,
            rawHeaders: request.rawHeaders,
            body: truncateBodyForChat(resolvedBody.body),
            httpVersion: request.httpVersion,
          });
        }),
      );
      const mentions = mentionResults.filter(
        (mention): mention is NonNullable<(typeof mentionResults)[number]> =>
          mention !== null,
      );
      const missingBodyCount = selectedBulkRequests.length - mentions.length;

      if (mentions.length === 0) {
        toasterApi.error({
          title: t('httpHistory.bulkActions.copilotErrorTitle'),
          description: t('httpHistory.bulkActions.copilotErrorDescription'),
        });

        return;
      }

      sendHttpMentionsToChat({
        mentions,
      });
      if (missingBodyCount > 0) {
        toasterApi.warning({
          title: t('httpHistory.bulkActions.copilotPartialTitle'),
          description: t('httpHistory.bulkActions.copilotPartialDescription', {
            succeeded: mentions.length,
            failed: missingBodyCount,
          }),
        });

        return;
      }

      toasterApi.success({
        title: t('httpHistory.bulkActions.copilotSuccessTitle'),
        description: t('httpHistory.bulkActions.copilotSuccessDescription', {
          count: mentions.length,
        }),
      });
    } catch (error) {
      console.error('Failed to send requests to Co-pilot:', error);
      toasterApi.error({
        title: t('httpHistory.bulkActions.copilotErrorTitle'),
        description: t('httpHistory.bulkActions.copilotErrorDescription'),
      });
    } finally {
      setIsBulkActionRunning(false);
    }
  }, [
    canRunBulkRequestAction,
    selectedBulkRequests,
    resolveRequestBodyForTransport,
    sendHttpMentionsToChat,
    t,
  ]);
  const handleOpenNeoExecution = useCallback(
    ({ threadId }: { threadId: string }) => {
      navigateInActiveTab({
        id: `executions-${threadId}`,
        title: 'Execution',
        type: 'executions',
        data: { threadId },
      });
    },
    [navigateInActiveTab],
  );
  const handleOpenNeoCopilotChat = useCallback(
    async ({ threadId }: { threadId: string }) => {
      let matchingConversation = conversations.find(
        (conversation) => conversation.threadId === threadId,
      );

      if (!matchingConversation) {
        try {
          const refetchResult = await refetchConversations();

          matchingConversation = refetchResult.data?.conversations.find(
            (conversation) => conversation.threadId === threadId,
          );
        } catch (error) {
          console.error('Failed to refetch conversations:', error);
        }
      }

      if (!matchingConversation) {
        toasterApi.info({
          title: 'Chat history unavailable',
          description:
            'Could not resolve the Copilot conversation for this request yet. Please try again.',
        });

        return;
      }

      const existingTab = chatTabs.find((tab) => tab.chatId === threadId);

      if (existingTab) {
        if (existingTab.conversationId !== matchingConversation.id) {
          updateChatTab(existingTab.tabId, {
            conversationId: matchingConversation.id,
            title: matchingConversation.title || existingTab.title || 'Chat',
          });
        }

        setActiveChatTabId(existingTab.tabId);
        setActiveConversation(matchingConversation.id);
      } else {
        addChatTab({
          tabId: threadId,
          chatId: threadId,
          conversationId: matchingConversation.id,
          title: matchingConversation.title || 'Chat',
          targetId,
          targetType,
          mentions: [],
        });
        setActiveChatTabId(threadId);
        setActiveConversation(matchingConversation.id);
      }

      setSidePanelsVisible(true);
      setRightPanelView('copilot');
      setChatCollapsed(false);
    },
    [
      chatTabs,
      updateChatTab,
      setActiveChatTabId,
      setActiveConversation,
      conversations,
      refetchConversations,
      addChatTab,
      targetId,
      targetType,
      setSidePanelsVisible,
      setRightPanelView,
      setChatCollapsed,
    ],
  );
  const handleOpenRelatedContext = useCallback(
    async (request: ProxyRequest) => {
      if (!request.threadId) {
        return;
      }

      if (request.source === 'copilot') {
        await handleOpenNeoCopilotChat({ threadId: request.threadId });

        return;
      }

      if (request.source === 'neo') {
        handleOpenNeoExecution({ threadId: request.threadId });
      }
    },
    [handleOpenNeoCopilotChat, handleOpenNeoExecution],
  );
  const handleAddToComparer = useCallback(
    async (request: ProxyRequest, type: 'request' | 'response') => {
      try {
        const bodies = await fetchBodies(request.id);
        const requestBody = bodies?.requestBody || request.body || '';
        const responseBody = bodies?.responseBody || request.responseBody || '';
        let pathname = request.url;

        try {
          const url = new URL(request.url);

          pathname = url.pathname;
        } catch {
          pathname = request.url;
        }

        const requestData = {
          id: request.id,
          method: request.method,
          url: request.url,
          headers: request.headers,
          body: requestBody,
          httpVersion: request.httpVersion,
          timestamp: request.timestamp,
          ...(type === 'response' && {
            status: request.status,
            responseHeaders: request.responseHeaders,
            responseBody: responseBody,
            responseHttpVersion: request.responseHttpVersion,
          }),
        };

        addRequestToComparer(
          requestData,
          `${request.method} ${pathname}`,
          type,
        );
      } catch (error) {
        console.error('Failed to add request to comparer:', error);
        toasterApi.error({
          title: 'Failed to add to comparer',
          description:
            error instanceof Error ? error.message : 'An error occurred',
        });
      }
    },
    [fetchBodies, addRequestToComparer],
  );

  useEffect(() => {
    return registerRequestGridHandler({
      tabId,
      getSelectedRequest: () => selectedRequest,
      sendToReplay: (request) => {
        const req = request as ProxyRequest;

        handleReplay(req);
      },
    });
  }, [tabId, selectedRequest, handleReplay]);
  const selectedRequestRef = useRef(selectedRequest);
  const gridApiRef = useRef(gridApi);

  selectedRequestRef.current = selectedRequest;
  gridApiRef.current = gridApi;
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const target = e.target as HTMLElement;

      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const api = gridApiRef.current;

      if (!api) return;
      const displayedRowCount = api.getDisplayedRowCount();

      if (displayedRowCount === 0) return;
      e.preventDefault();
      const currentId = selectedRequestRef.current?.id;
      let currentIndex = -1;

      if (currentId) {
        const rowNode = api.getRowNode(currentId);

        if (rowNode?.rowIndex != null) {
          currentIndex = rowNode.rowIndex;
        }
      }

      const newIndex =
        e.key === 'ArrowDown'
          ? currentIndex === -1
            ? 0
            : Math.min(currentIndex + 1, displayedRowCount - 1)
          : currentIndex === -1
            ? displayedRowCount - 1
            : Math.max(currentIndex - 1, 0);
      const newRowNode = api.getDisplayedRowAtIndex(newIndex);

      if (newRowNode?.data) {
        setSelectedRequest(newRowNode.data);
        api.ensureIndexVisible(newIndex, 'middle');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setSelectedRequest]);
  const handleClearHistory = useCallback(async () => {
    await clearHistory();
    setSelectedRequest(null);
    setSelectedBulkRequests([]);
    setAvailableBulkRequestCount(0);
    lastPositiveCountRef.current = 0;
    gridApi?.deselectAll();
  }, [clearHistory, gridApi, setSelectedRequest]);
  const getPathFromUrl = useCallback((url: string): string => {
    const parsed = getParsedUrl(url);

    return parsed ? parsed.pathname + parsed.search : url;
  }, []);
  const hasQueryParams = useCallback((url: string): boolean => {
    const parsed = getParsedUrl(url);

    return parsed ? parsed.search.length > 0 : false;
  }, []);
  const getExtensionFromUrl = useCallback((url: string): string => {
    const parsed = getParsedUrl(url);

    return parsed?.extension ?? '';
  }, []);
  const getMimeType = useCallback((request: ProxyRequest): string => {
    if (!request.responseHeaders) return '';
    const contentType = request.responseHeaders['content-type'];

    if (!contentType) return '';
    const typeValue = Array.isArray(contentType) ? contentType[0] : contentType;

    return typeValue.split(';')[0].trim();
  }, []);
  const defaultColDef = useMemo(
    () => ({ sortable: true, filter: false, resizable: true }),
    [],
  );
  const getRowId = useCallback(
    (params: { data: ProxyRequest }) => params.data.id,
    [],
  );
  const getAvailableBulkRequestCount = useCallback(
    (api: GridApi<ProxyRequest>) => {
      let availableCount = 0;

      api.forEachNode((node: IRowNode<ProxyRequest>) => {
        if (node.data) {
          availableCount += 1;
        }
      });

      return availableCount;
    },
    [],
  );
  const syncBulkSelectionState = useCallback(
    (api: GridApi<ProxyRequest>) => {
      const selectedRows = api
        .getSelectedRows()
        .filter((request): request is ProxyRequest => Boolean(request?.id));

      setSelectedBulkRequests(selectedRows);
      setAvailableBulkRequestCount(getAvailableBulkRequestCount(api));
    },
    [getAvailableBulkRequestCount],
  );
  const handleBulkSelectionChanged = useCallback(
    (event: SelectionChangedEvent<ProxyRequest>) => {
      syncBulkSelectionState(event.api);
    },
    [syncBulkSelectionState],
  );
  const handleModelUpdated = useCallback(
    (event: ModelUpdatedEvent<ProxyRequest>) => {
      syncBulkSelectionState(event.api);
    },
    [syncBulkSelectionState],
  );
  const handleSelectAllBulkRequests = useCallback(
    (selected: boolean) => {
      if (!gridApi || gridApi.isDestroyed()) {
        return;
      }

      gridApi.forEachNode((node: IRowNode<ProxyRequest>) => {
        if (node.data) {
          node.setSelected(selected);
        }
      });
      syncBulkSelectionState(gridApi);
    },
    [gridApi, syncBulkSelectionState],
  );
  const applyPinnedUtilityColumnOrder = useCallback(
    (api: GridApi<ProxyRequest>) => {
      const columns = api.getColumns();

      if (!columns) {
        return;
      }

      const orderedColumnIds = [
        REQUEST_GRID_COLUMN_ID.HIGHLIGHT,
        REQUEST_GRID_COLUMN_ID.BULK_SELECT,
        ...columns
          .map((column) => column.getColId())
          .filter(
            (colId) =>
              colId !== REQUEST_GRID_COLUMN_ID.HIGHLIGHT &&
              colId !== REQUEST_GRID_COLUMN_ID.BULK_SELECT,
          ),
      ];

      api.applyColumnState({
        state: orderedColumnIds.map((colId) => ({
          colId,
          ...(colId === REQUEST_GRID_COLUMN_ID.HIGHLIGHT ||
          colId === REQUEST_GRID_COLUMN_ID.BULK_SELECT
            ? { pinned: 'left' as const }
            : {}),
        })),
        applyOrder: true,
      });
    },
    [],
  );
  const persistence = useMemo(
    () =>
      userId
        ? {
            tableId: TABLE_IDS.PROXY_GLOBAL_REQUESTS,
            userId,
            onPersistedStateApplied: (api: GridApi<ProxyRequest>) => {
              applyPinnedUtilityColumnOrder(api);
              applyTabSortState(api);
            },
          }
        : undefined,
    [userId, applyPinnedUtilityColumnOrder, applyTabSortState],
  );
  const handleSortChanged = useCallback(
    (event: SortChangedEvent<ProxyRequest>) => {
      if (isApplyingPersistedSortRef.current) {
        return;
      }

      const columnState = event.api.getColumnState();
      const hasActiveSort = columnState.some((column) => column.sort != null);
      const sortedColumn = columnState.find(
        (column) =>
          column.sort != null &&
          typeof column.colId === 'string' &&
          isRequestsSortField(column.colId),
      );

      if (!hasActiveSort) {
        if (sortField === 'timestamp' && sortOrder === 'desc') {
          return;
        }

        const { setGridScrollAnchor, clearScrollRestorePending } =
          useTabViewStateStore.getState();

        setGridScrollAnchor(historyScrollId, {
          scrollTop: 0,
          firstRowIndex: 0,
        });
        clearScrollRestorePending(historyScrollId);
        setSortField('timestamp');
        setSortOrder('desc');

        return;
      }

      if (
        !sortedColumn?.colId ||
        !isRequestsSortField(sortedColumn.colId) ||
        (sortedColumn.colId === sortField && sortedColumn.sort === sortOrder)
      ) {
        return;
      }

      const nextSortOrder =
        sortedColumn.sort === 'asc' || sortedColumn.sort === 'desc'
          ? sortedColumn.sort
          : 'desc';
      const { setGridScrollAnchor, clearScrollRestorePending } =
        useTabViewStateStore.getState();

      setGridScrollAnchor(historyScrollId, {
        scrollTop: 0,
        firstRowIndex: 0,
      });
      clearScrollRestorePending(historyScrollId);
      setSortField(sortedColumn.colId);
      setSortOrder(nextSortOrder);
    },
    [historyScrollId, setSortField, setSortOrder, sortField, sortOrder],
  );
  const handleGridReady = useCallback(
    (params: { api: GridApi<ProxyRequest> }) => {
      setGridApi(params.api);
      applyPinnedUtilityColumnOrder(params.api);
      applyTabSortState(params.api);
      if (datasource) {
        params.api.setGridOption('datasource', datasource);
      }

      syncBulkSelectionState(params.api);
    },
    [
      applyTabSortState,
      datasource,
      syncBulkSelectionState,
      applyPinnedUtilityColumnOrder,
    ],
  );
  const handleCellClicked = useCallback(
    (event: {
      column?: {
        getColId: () => string;
      };
      data?: ProxyRequest;
    }) => {
      const colId = event.column?.getColId();

      if (
        colId === REQUEST_GRID_COLUMN_ID.ACTIONS ||
        colId === REQUEST_GRID_COLUMN_ID.NOTES ||
        colId === REQUEST_GRID_COLUMN_ID.BULK_SELECT
      ) {
        return;
      }

      if (event.data) {
        const currentId = selectedRequestRef.current?.id;

        setSelectedRequest(currentId === event.data.id ? null : event.data);
      }
    },
    [setSelectedRequest],
  );
  const handleCellContextMenu = useCallback(
    (event: { data?: ProxyRequest; event?: Event | null }) => {
      if (event.data && event.event) {
        event.event.preventDefault();
        event.event.stopPropagation();
        const mouseEvent = event.event as MouseEvent;

        setRowContextMenuPosition({
          x: mouseEvent.clientX,
          y: mouseEvent.clientY,
        });
        setRowContextMenuRequestId(event.data.id);
        setRowContextMenuOpen(true);
      }
    },
    [],
  );
  const DEFAULT_ROW_STYLE = useMemo(
    () =>
      ({ cursor: 'pointer' }) as {
        cursor: string;
        background?: string;
      },
    [],
  );
  const SELECTED_ROW_STYLE = useMemo(
    () =>
      ({
        cursor: 'pointer',
        background: token('colors.bg.subtle'),
      }) as {
        cursor: string;
        background?: string;
      },
    [],
  );
  const getRowStyle = useCallback(
    (params: {
      data?: ProxyRequest;
    }): {
      cursor: string;
      background?: string;
    } => {
      const currentSelectedId = selectedRequestRef.current?.id;

      if (params.data && currentSelectedId === params.data.id) {
        return SELECTED_ROW_STYLE;
      }

      if (params.data?.wasProxyModified) {
        return { cursor: 'pointer', background: '#f9731620' };
      }

      if (params.data?.highlight) {
        return {
          cursor: 'pointer',
          background: getHighlightRowTint(params.data.highlight),
        };
      }

      return DEFAULT_ROW_STYLE;
    },
    [DEFAULT_ROW_STYLE, SELECTED_ROW_STYLE],
  );
  const columnDefs = useMemo<ColDef<ProxyRequest>[]>(
    () => [
      {
        headerName: '',
        colId: REQUEST_GRID_COLUMN_ID.HIGHLIGHT,
        initialWidth: 6,
        minWidth: 6,
        maxWidth: 6,
        resizable: false,
        pinned: 'left',
        lockPosition: true,
        sortable: false,
        cellStyle: {
          padding: 0,
        } as CellStyle,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data?.highlight) return null;

          return (
            <Box
              w="full"
              h="full"
              style={{
                backgroundColor: getHighlightHex(params.data.highlight),
              }}
            />
          );
        },
        suppressNavigable: true,
        valueGetter: (params) => params.data?.highlight || '',
      },
      {
        headerName: '',
        colId: REQUEST_GRID_COLUMN_ID.BULK_SELECT,
        initialWidth: 42,
        minWidth: 42,
        maxWidth: 42,
        pinned: 'left',
        lockPosition: true,
        sortable: false,
        filter: false,
        resizable: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        cellClass: 'no-row-click',
        suppressNavigable: true,
      },
      {
        headerName: '#',
        field: 'sequentialId',
        initialWidth: 60,
        pinned: 'left',
        lockPosition: true,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;

          return (
            <HStack gap="1" alignItems="center" h="full">
              <MonoSmall color="fg.muted">{params.data.sequentialId}</MonoSmall>
              {params.data.wasEdited && (
                <Tooltip
                  title="This request was edited during interception"
                  positioning={{ strategy: 'fixed' }}
                >
                  <Box display="flex" alignItems="center">
                    <Edit03Icon size={10} />
                  </Box>
                </Tooltip>
              )}
              {params.data.wasProxyModified && !params.data.wasEdited && (
                <Tooltip
                  title="Modified by proxy match and replace"
                  positioning={{ strategy: 'fixed' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg="#f97316"
                  />
                </Tooltip>
              )}
            </HStack>
          );
        },
        suppressNavigable: true,
        sortable: true,
        filter: false,
        comparator: (
          valueA: number | undefined,
          valueB: number | undefined,
        ) => {
          const a = valueA ?? 0;
          const b = valueB ?? 0;

          return a - b;
        },
      },
      {
        headerName: 'Time',
        field: 'timestamp',
        initialWidth: 130,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;

          return (
            <MonoSmall color="fg.muted">
              {formatTime(params.data.timestamp)}
            </MonoSmall>
          );
        },
      },
      {
        headerName: 'Host',
        field: 'host',
        initialWidth: 150,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;

          return <MonoSmall color="fg.default">{params.data.host}</MonoSmall>;
        },
      },
      {
        headerName: 'Method',
        field: 'method',
        initialWidth: 80,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const severity = getMethodSeverity(params.data.method);

          return severity ? (
            <BadgeSeverity severity={severity} size="sm">
              {params.data.method}
            </BadgeSeverity>
          ) : (
            <Badge size="sm" variant="fill">
              {params.data.method}
            </Badge>
          );
        },
      },
      {
        headerName: 'Source',
        field: 'source',
        initialWidth: 120,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const source = params.data.source || 'proxy';
          const config = getSourceConfig(source);
          const Icon = config.icon;

          return (
            <Badge size="sm" variant="emphasis" icon={<Icon size={12} />}>
              {config.label}
            </Badge>
          );
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        initialWidth: 80,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const severity = getStatusSeverity(params.data.status);
          const hasWarnings = (params.data.proxyWarnings?.length || 0) > 0;
          const hasError = Boolean(params.data.proxyError);

          return (
            <HStack gap="1" alignItems="center" h="full">
              {params.data.status ? (
                severity ? (
                  <BadgeSeverity severity={severity} size="sm">
                    {params.data.status}
                  </BadgeSeverity>
                ) : (
                  <Badge size="sm" variant="fill">
                    {params.data.status}
                  </Badge>
                )
              ) : (
                <MetaDefault color="fg.muted">-</MetaDefault>
              )}
              {hasError ? (
                <Tooltip
                  title={params.data.proxyError}
                  positioning={{ strategy: 'fixed' }}
                >
                  <Box display="flex" alignItems="center">
                    <AlertCircleIcon
                      size={12}
                      color={token('colors.fg.error')}
                    />
                  </Box>
                </Tooltip>
              ) : null}
              {hasWarnings ? (
                <Tooltip
                  title={(params.data.proxyWarnings || []).join(' ')}
                  positioning={{ strategy: 'fixed' }}
                >
                  <Box display="flex" alignItems="center">
                    <Alert01Icon
                      size={12}
                      color={token('colors.border.neutral.warning')}
                    />
                  </Box>
                </Tooltip>
              ) : null}
            </HStack>
          );
        },
      },
      {
        headerName: 'Path',
        field: 'url',
        initialWidth: 400,
        minWidth: 200,
        valueGetter: (params) => {
          if (!params.data) return '';

          return getPathFromUrl(params.data.url);
        },
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const path = getPathFromUrl(params.data.url);

          return (
            <MonoSmall color="fg.default" truncate title={path}>
              {path}
            </MonoSmall>
          );
        },
      },
      {
        headerName: 'Params',
        field: 'url',
        initialWidth: 60,
        valueGetter: (params) => {
          if (!params.data) return '';

          return hasQueryParams(params.data.url) ? '✓' : '';
        },
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;

          return (
            <MetaDefault color="fg.muted" textAlign="center">
              {hasQueryParams(params.data.url) ? '✓' : ''}
            </MetaDefault>
          );
        },
      },
      {
        headerName: t('httpHistory.table.intercepted'),
        field: 'wasIntercepted',
        initialWidth: 95,
        valueGetter: (params) => {
          if (!params.data) return '';

          return params.data.wasIntercepted ? '✓' : '';
        },
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;

          return (
            <MetaDefault color="fg.muted" textAlign="center">
              {params.data.wasIntercepted ? '✓' : ''}
            </MetaDefault>
          );
        },
      },
      {
        headerName: 'Size',
        field: 'length',
        initialWidth: 80,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;

          return (
            <MetaDefault color="fg.muted">
              {formatFileSize(params.data.length)}
            </MetaDefault>
          );
        },
        comparator: (
          valueA: number | undefined,
          valueB: number | undefined,
        ) => {
          const a = valueA ?? 0;
          const b = valueB ?? 0;

          return a - b;
        },
      },
      {
        headerName: 'MIME Type',
        field: 'responseHeaders',
        initialWidth: 120,
        valueGetter: (params) => {
          if (!params.data) return '';

          return getMimeType(params.data);
        },
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const mimeType = getMimeType(params.data);

          return (
            <MetaDefault color="fg.muted" truncate title={mimeType}>
              {mimeType || '-'}
            </MetaDefault>
          );
        },
      },
      {
        headerName: 'Extension',
        field: 'url',
        initialWidth: 70,
        valueGetter: (params) => {
          if (!params.data) return '';

          return getExtensionFromUrl(params.data.url);
        },
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const extension = getExtensionFromUrl(params.data.url);

          return <MonoSmall color="fg.muted">{extension || '-'}</MonoSmall>;
        },
      },
      {
        headerName: 'Notes',
        colId: REQUEST_GRID_COLUMN_ID.NOTES,
        initialWidth: 150,
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const hasComment = !!params.data.comment;
          const tags = params.data.tags || [];

          return (
            <HStack gap="2" alignItems="center" w="full" h="full">
              {/* Tags */}
              {tags.length > 0 && (
                <HStack gap="1" flex="1" minW="0" alignItems="center">
                  {tags.slice(0, 2).map((tag) => (
                    <Badge
                      key={tag}
                      size="sm"
                      variant="emphasis"
                      flexShrink={0}
                    >
                      {tag}
                    </Badge>
                  ))}
                  {tags.length > 2 && (
                    <Tooltip
                      title={`${tags.length - 2} more tags: ${tags.slice(2).join(', ')}`}
                      positioning={{ strategy: 'fixed' }}
                    >
                      <Badge size="sm" variant="emphasis" flexShrink={0}>
                        {`+${tags.length - 2}`}
                      </Badge>
                    </Tooltip>
                  )}
                </HStack>
              )}

              {/* Comment Icon */}
              {hasComment && (
                <Tooltip
                  title={params.data.comment}
                  positioning={{ strategy: 'fixed' }}
                >
                  <Box
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    cursor="help"
                  >
                    <Message01Icon size={16} />
                  </Box>
                </Tooltip>
              )}
            </HStack>
          );
        },
        suppressNavigable: true,
        valueGetter: (params) => {
          if (!params.data) return '';
          const tags = params.data.tags?.join(',') || '';
          const comment = params.data.comment || '';

          return `${tags}${comment}`;
        },
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        } as CellStyle,
      },
      {
        headerName: 'Actions',
        colId: REQUEST_GRID_COLUMN_ID.ACTIONS,
        initialWidth: 80,
        minWidth: 80,
        pinned: 'right',
        suppressNavigable: true,
        editable: false,
        cellClass: 'no-row-click',
        cellRenderer: (params: ICellRendererParams<ProxyRequest>) => {
          if (!params.data) return null;
          const request = params.data;

          return (
            <HStack
              gap="1"
              justifyContent="center"
              h="full"
              alignItems="center"
            >
              <Tooltip title={t('httpHistory.actions.menuTooltip')}>
                <IconButton
                  aria-label="More actions"
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = (e.target as HTMLElement)
                      .closest('button')
                      ?.getBoundingClientRect();

                    if (rect) {
                      setMenuPosition({ x: rect.right, y: rect.bottom });
                    }

                    setMenuRequestId(request.id);
                    setMenuOpen(true);
                  }}
                >
                  <MoreVerticalCircle01Icon size={14} />
                </IconButton>
              </Tooltip>
            </HStack>
          );
        },
        filter: false,
        sortable: false,
      },
    ],
    [getPathFromUrl, hasQueryParams, getExtensionFromUrl, getMimeType, t],
  );
  const handleRequestActionMenuSelect = useCallback(
    ({
      value,
      requestId,
      closeMenu,
    }: {
      value: string;
      requestId: string | null;
      closeMenu: () => void;
    }) => {
      if (!requestId) {
        closeMenu();

        return;
      }

      switch (value) {
        case 'add-to-copilot': {
          const request = getGridRequest(requestId);

          if (request) {
            handleAddToContext(request);
          }

          closeMenu();
          break;
        }

        case 'track-request': {
          const request = getGridRequest(requestId);

          if (request) {
            void handleTrackRequest(request);
          }

          closeMenu();
          break;
        }

        case 'replay-request': {
          const request = getGridRequest(requestId);

          if (request) {
            void handleReplay(request);
          }

          closeMenu();
          break;
        }

        case 'open-related-context': {
          const request = getGridRequest(requestId);

          if (request) {
            void handleOpenRelatedContext(request);
          }

          closeMenu();
          break;
        }

        case 'show-context': {
          const request = getGridRequest(requestId);

          closeMenu();
          if (!request) {
            break;
          }

          void (async () => {
            const timestampMs =
              request.timestamp instanceof Date
                ? request.timestamp.getTime()
                : new Date(request.timestamp).getTime();
            const excludeRules = useExclusionRulesStore
              .getState()
              .rules.filter((rule) => rule.enabled && rule.type === 'exclude')
              .map((rule) => ({
                pattern: rule.pattern,
                isRegex: rule.isRegex,
              }));
            const proxyState = useProxyStore.getState();
            const projectState = useProjectStore.getState();
            const {
              targetId: resolvedTargetId,
              targetType: resolvedTargetType,
            } = resolveHistoryTarget({
              applicationIdProp: targetId,
              projectTargetId: projectState.targetId,
              projectTargetType: projectState.targetType,
              storeTargetId: proxyState.currentTargetId,
              storeTargetType: proxyState.currentTargetType,
            });
            let countResult;

            try {
              countResult = await window.electronAPI?.history?.count({
                targetId: resolvedTargetId,
                targetType: resolvedTargetType,
                filters: {
                  tqlWhereClause: 'r.timestamp > ?',
                  tqlParams: [timestampMs],
                  ...(applicationScope ? { applicationScope } : {}),
                  ...(excludeRules.length > 0 ? { excludeRules } : {}),
                },
              });
            } catch (error) {
              console.error('Failed to count HTTP history for show context', {
                error:
                  error instanceof Error ? error : new Error(String(error)),
                requestId,
              });
              toasterApi.error({
                title: t('httpHistory.actions.showContextErrorTitle'),
                description: t(
                  'httpHistory.actions.showContextErrorDescription',
                ),
              });

              return;
            }

            if (!countResult?.success || !countResult.data) {
              toasterApi.error({
                title: t('httpHistory.actions.showContextErrorTitle'),
                description: t(
                  'httpHistory.actions.showContextErrorDescription',
                ),
              });

              return;
            }

            setSelectedRequest(request);
            resetFilters();
            useFilterPresetsStore.getState().setActiveQuery('');
            const api = gridApiRef.current;

            if (!api || api.isDestroyed()) {
              return;
            }

            const fallbackIndex = countResult.data.count;
            let safetyTimeoutId: ReturnType<typeof setTimeout> | null = null;
            const handleShowContextModelUpdated = () => {
              if (safetyTimeoutId) {
                clearTimeout(safetyTimeoutId);
                safetyTimeoutId = null;
              }

              api.removeEventListener(
                'modelUpdated',
                handleShowContextModelUpdated,
              );
              const rowNode = api.getRowNode(requestId);

              if (rowNode?.rowIndex != null) {
                api.ensureIndexVisible(rowNode.rowIndex, 'middle');

                return;
              }

              const rowCount = api.getDisplayedRowCount();
              const clampedIndex =
                rowCount > 0
                  ? Math.min(Math.max(fallbackIndex, 0), rowCount - 1)
                  : fallbackIndex;

              api.ensureIndexVisible(clampedIndex, 'middle');
            };

            api.addEventListener('modelUpdated', handleShowContextModelUpdated);
            safetyTimeoutId = setTimeout(() => {
              safetyTimeoutId = null;
              if (!api.isDestroyed()) {
                api.removeEventListener(
                  'modelUpdated',
                  handleShowContextModelUpdated,
                );
              }
            }, 1000);
          })().catch((error: unknown) => {
            console.error('Failed to show request context in HTTP history', {
              error: error instanceof Error ? error : new Error(String(error)),
              requestId,
            });
          });
          break;
        }

        case 'highlight-red':
        case 'highlight-orange':
        case 'highlight-yellow':
        case 'highlight-green':
        case 'highlight-blue':
        case 'highlight-purple':
        case 'highlight-gray': {
          const request = getGridRequest(requestId);
          const color = value.replace('highlight-', '');

          setRequestHighlight(requestId, color as HighlightColor, request);
          closeMenu();
          break;
        }

        case 'highlight-clear': {
          const request = getGridRequest(requestId);

          setRequestHighlight(requestId, undefined, request);
          closeMenu();
          break;
        }

        case 'add-comment':
          setAnnotatingRequestId(requestId);
          setCommentDialogOpen(true);
          closeMenu();
          break;
        case 'add-tags':
          setAnnotatingRequestId(requestId);
          setTagsDialogOpen(true);
          closeMenu();
          break;
        case 'send-to-comparer-request':
        case 'send-to-comparer-response': {
          const request = getGridRequest(requestId);

          if (request) {
            const type =
              value === 'send-to-comparer-request' ? 'request' : 'response';

            handleAddToComparer(request, type);
          }

          closeMenu();
          break;
        }
      }
    },
    [
      applicationScope,
      getGridRequest,
      handleAddToComparer,
      handleAddToContext,
      handleOpenRelatedContext,
      handleReplay,
      handleTrackRequest,
      resetFilters,
      setRequestHighlight,
      setSelectedRequest,
      t,
      targetId,
    ],
  );

  useEffect(() => {
    if (
      filteredCount === 0 &&
      !isLoadingGrid &&
      !isLoadingCount &&
      selectedRequest
    ) {
      setSelectedRequest(null);
    }
  }, [
    filteredCount,
    isLoadingGrid,
    isLoadingCount,
    selectedRequest,
    setSelectedRequest,
  ]);
  const showEmptyState = shouldShowHttpHistoryEmptyState({
    filteredCount,
    isLoadingCount,
    lastPositiveCount: lastPositiveCountRef.current,
  });
  const allBulkRequestsSelected =
    availableBulkRequestCount > 0 &&
    selectedBulkRequestCount === availableBulkRequestCount;
  const hasPartialBulkSelection =
    selectedBulkRequestCount > 0 && !allBulkRequestsSelected;
  const menuRequest = menuRequestId ? getGridRequest(menuRequestId) : undefined;
  const rowContextMenuRequest = rowContextMenuRequestId
    ? getGridRequest(rowContextMenuRequestId)
    : undefined;

  return (
    <VStack
      data-tour-id="http-history-panel"
      gap="0"
      h="full"
      w="full"
      minW="0"
      alignItems="stretch"
      overflow="hidden"
    >
      <RequestsFilterBar
        onClearHistory={handleClearHistory}
        requestCount={filteredCount ?? 0}
        showOnlyInScope={showOnlyInScope}
        onShowOnlyInScopeChange={handleShowOnlyInScopeChange}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
      />

      <HttpHistoryBulkActionsBar
        selectedCount={selectedBulkRequestCount}
        availableCount={availableBulkRequestCount}
        allSelected={allBulkRequestsSelected}
        hasPartialSelection={hasPartialBulkSelection}
        isSendToTrackingLoading={isBulkTracking}
        isSendToCopilotLoading={isBulkActionRunning}
        hideSendToTracking={boothTourEnabled}
        onSelectAll={handleSelectAllBulkRequests}
        onSendToTracking={handleTrackSelectedRequests}
        onSendToCopilot={handleSendSelectedToCopilot}
      />

      <Box flex="1" minW="0" overflow="hidden">
        {showEmptyState ? (
          <EmptyState
            icon={HierarchyIcon}
            title="No Requests Captured Yet"
            description="Start the proxy server to begin monitoring HTTP traffic. Requests will appear here as they are captured."
          />
        ) : (
          <Splitter.Root
            orientation="vertical"
            h="100%"
            w="full"
            defaultSize={globalRequestsSplitterSizes.map(({ size }) => size)}
            panels={globalRequestsSplitterSizes.map(({ id }) => ({
              id: id.toString(),
            }))}
            onResizeEnd={({ size }) => {
              setGlobalRequestsSplitterSizes(
                globalRequestsSplitterSizes.map((panel, index) => ({
                  ...panel,
                  size: size[index],
                })),
              );
            }}
            borderWidth="0"
            gap={0}
            minW="0"
          >
            <Splitter.Panel
              id="table"
              borderWidth="0"
              h="full"
              w="full"
              minW="0"
            >
              <Box
                ref={gridContainerRef}
                h="full"
                w="full"
                overflow="hidden"
                style={{ position: 'relative', zIndex: 1 }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <Table
                  columnDefs={columnDefs}
                  rowModelType={'infinite' as const}
                  datasource={datasource ?? undefined}
                  cacheBlockSize={cacheBlockSize}
                  maxBlocksInCache={maxBlocksInCache}
                  cacheOverflowSize={2}
                  infiniteInitialRowCount={1}
                  loading={isLoadingGrid && lastPositiveCountRef.current === 0}
                  onGridReady={handleGridReady}
                  onSortChanged={handleSortChanged}
                  rowHeight={ROW_HEIGHT}
                  headerHeight={32}
                  noBorder={true}
                  fontSize={12}
                  headerFontSize={11}
                  animateRows={false}
                  defaultColDef={defaultColDef}
                  suppressCellFocus={true}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  getRowId={getRowId}
                  rowBuffer={100}
                  debounceVerticalScrollbar={true}
                  suppressColumnVirtualisation={false}
                  persistence={persistence}
                  hideColumnControls={true}
                  fitColumnsOnGridSizeChanged={false}
                  onCellClicked={handleCellClicked}
                  onCellContextMenu={handleCellContextMenu}
                  onSelectionChanged={handleBulkSelectionChanged}
                  onModelUpdated={handleModelUpdated}
                  getRowStyle={getRowStyle}
                  suppressScrollOnNewData={true}
                />
              </Box>
            </Splitter.Panel>

            {selectedRequest && (
              <>
                <Splitter.ResizeTrigger
                  id="table:details"
                  height="4px"
                  minHeight="4px"
                  bg="transparent"
                  position="relative"
                  cursor="row-resize"
                  zIndex={10}
                  _after={{
                    content: '""',
                    position: 'absolute',
                    inset: '-4px 0 -4px 0',
                    height: '12px',
                    bg: 'transparent',
                    cursor: 'row-resize',
                    zIndex: 10,
                  }}
                  _hover={{
                    bg: 'border.default',
                    _after: {
                      bg: 'border.default',
                    },
                  }}
                />

                <Splitter.Panel id="details" borderWidth="0" h="100%" w="full">
                  <GlobalRequestDetailsPanel
                    applicationId={targetId ?? undefined}
                    selectedRequest={selectedRequest}
                    requestBody={requestLazyBody.body}
                    responseBody={responseLazyBody.body}
                    requestLazyBody={requestLazyBody}
                    responseLazyBody={responseLazyBody}
                    getBodies={getBodies}
                    onReplay={handleReplay}
                    openDecoderWith={openDecoderWith}
                    appendToInput={appendToInput}
                  />
                </Splitter.Panel>
              </>
            )}
          </Splitter.Root>
        )}
      </Box>

      {/* Annotations Menu (outside table for proper z-index) */}
      {menuRequestId && (
        <Menu.Root
          open={menuOpen}
          onOpenChange={({ open }) => {
            setMenuOpen(open);
            if (!open) {
              setMenuRequestId(null);
              setMenuPosition(null);
            }
          }}
          positioning={{
            strategy: 'fixed',
            placement: 'bottom-start',
            ...(menuPosition && {
              getAnchorRect: () => ({
                x: menuPosition.x,
                y: menuPosition.y,
                width: 0,
                height: 0,
              }),
            }),
          }}
          onSelect={(details) => {
            handleRequestActionMenuSelect({
              value: details.value,
              requestId: menuRequestId,
              closeMenu: () => setMenuOpen(false),
            });
          }}
        >
          <Menu.Positioner>
            <Menu.Content minW="180px" py="1">
              <HttpRequestActionsMenuContent
                request={menuRequest}
                iconSize={14}
                onSelect={(value, selectedRequestId) =>
                  handleRequestActionMenuSelect({
                    value,
                    requestId: selectedRequestId ?? menuRequestId,
                    closeMenu: () => setMenuOpen(false),
                  })
                }
              />
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      )}

      {/* Row Right-Click Context Menu */}
      {rowContextMenuRequestId && (
        <Menu.Root
          open={rowContextMenuOpen}
          onOpenChange={({ open }) => {
            setRowContextMenuOpen(open);
            if (!open) {
              setRowContextMenuRequestId(null);
              setRowContextMenuPosition(null);
            }
          }}
          positioning={{
            strategy: 'fixed',
            placement: 'bottom-start',
            ...(rowContextMenuPosition && {
              getAnchorRect: () => ({
                x: rowContextMenuPosition.x,
                y: rowContextMenuPosition.y,
                width: 0,
                height: 0,
              }),
            }),
          }}
          onSelect={(details) => {
            handleRequestActionMenuSelect({
              value: details.value,
              requestId: rowContextMenuRequestId,
              closeMenu: () => setRowContextMenuOpen(false),
            });
          }}
        >
          <Menu.Positioner>
            <Menu.Content minW="160px" py="1">
              <HttpRequestActionsMenuContent
                request={rowContextMenuRequest}
                iconSize={12}
                itemFontSize="xs"
                onSelect={(value, selectedRequestId) =>
                  handleRequestActionMenuSelect({
                    value,
                    requestId: selectedRequestId ?? rowContextMenuRequestId,
                    closeMenu: () => setRowContextMenuOpen(false),
                  })
                }
              />
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      )}

      {/* Annotation Dialogs */}
      {annotatingRequestId && (
        <>
          <CommentDialog
            isOpen={commentDialogOpen}
            onClose={() => {
              setCommentDialogOpen(false);
              setAnnotatingRequestId(null);
            }}
            requestId={annotatingRequestId}
            currentComment={getGridRequest(annotatingRequestId)?.comment}
          />
          <TagsDialog
            isOpen={tagsDialogOpen}
            onClose={() => {
              setTagsDialogOpen(false);
              setAnnotatingRequestId(null);
            }}
            requestId={annotatingRequestId}
            currentTags={getGridRequest(annotatingRequestId)?.tags}
          />
        </>
      )}
      <SelectHttpContextForChatDialog
        state={httpContextDialogState}
        onClose={handleHttpContextDialogClose}
      />
    </VStack>
  );
}
